import { CheckoutWidgetsBuilderState } from "@redotech/redo-conversion-core-common/checkouts/checkout-widgets-builder";
import { CheckoutOptimizationWidgetType } from "@redotech/redo-conversion-core-common/checkouts/widget";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { TextAndSupportingText } from "@redotech/redo-web/text-and-supporting-text";
import { BorderRadiusValue } from "@redotech/redo-web/theme/box";
import { memo, ReactNode } from "react";
import * as css from "./render-after-shipping-option-widget-preview.module.css";

export const RenderAfterShippingOptionWidgetPreview = memo(
  function RenderAfterShippingOptionWidgetPreview({
    widget,
  }: {
    widget: NonNullable<
      CheckoutWidgetsBuilderState["checkoutWidgets"][CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT]
    >;
  }) {
    return (
      <Flex dir="column" gap="none">
        <Flex
          borderTopLeftRadius="lg"
          borderTopRightRadius="lg"
          className={css.renderAfterShippingOptionWidgetPreviewSelected}
          dir="column"
          gap="xl"
          p="xl"
        >
          <ExampleShippingOptionContent selected />
        </Flex>
        <Flex
          bgColor="tertiary"
          borderColor="primary"
          borderStyle="solid"
          borderWidth="1px"
          p="xl"
        >
          {/* Widget preview - rendered after the shipping option */}
          <Flex
            align="center"
            borderRadius="xl"
            dir="row"
            gap="md"
            p="md"
            style={{ backgroundColor: widget.backgroundColor }}
            w="full"
          >
            {widget.imageUrl && (
              <img
                alt={widget.title}
                className={css.shippingOptionImage}
                src={widget.imageUrl}
              />
            )}
            <Flex dir="column" gap="xs">
              {widget.title && (
                <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                  {widget.title}
                </Text>
              )}
              {widget.description && (
                <Text fontSize="xs" textColor="secondary">
                  {widget.description}
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
        <ExampleShippingOption bottomRadii="none" topRadii="none" />
        <ExampleShippingOption bottomRadii="lg" topRadii="none" />
      </Flex>
    );
  },
);

export const ExampleShippingOptionContent = memo(
  function ExampleShippingOptionContent({
    selected = false,
  }: {
    selected?: boolean;
  }) {
    return (
      <Flex align="center" dir="row" justify="space-between">
        <Flex align="center" dir="row" gap="xl">
          {selected ? (
            <Flex borderRadius="full" className={css.selectedCircle} />
          ) : (
            <Flex
              borderColor="primary"
              borderRadius="full"
              borderStyle="solid"
              borderWidth="1px"
              className={css.grayCircle}
            />
          )}
          <TextAndSupportingText
            mainTextSize="sm"
            supportingText="[Shipping description]"
            supportingTextSize="xs"
            text="[Shipping name]"
          />
        </Flex>
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          [Price]
        </Text>
      </Flex>
    );
  },
);

export const ExampleShippingOption = memo(function ExampleShippingOption({
  topRadii,
  bottomRadii,
  children,
}: {
  topRadii: BorderRadiusValue;
  bottomRadii: BorderRadiusValue;
  children?: ReactNode;
}) {
  return (
    <Flex
      bgColor="primary"
      borderBottomLeftRadius={bottomRadii}
      borderBottomRightRadius={bottomRadii}
      borderColor="primary"
      borderStyle="solid"
      borderTopLeftRadius={topRadii}
      borderTopRightRadius={topRadii}
      borderWidth="1px"
      className={css.shippingOptionWidgetPreview}
      dir="column"
      gap="xl"
      p="xl"
    >
      <ExampleShippingOptionContent />
      {children}
    </Flex>
  );
});
